import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";
import {
  Car, Users, Calendar, MapPin, Shield, BarChart3, FileText, Bell,
  ArrowRight, Check, Menu, X, Zap, Building2, Star,
} from "lucide-react";

const NAV = [
  { label: "Features", href: "#features" },
  { label: "Pricing", href: "#pricing" },
  { label: "Customers", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
];

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-asphalt/90 backdrop-blur border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <span className="h-8 w-8 bg-cab flex items-center justify-center rounded font-display font-black text-asphalt text-lg">W</span>
          <span className="font-display font-bold text-white tracking-tight text-lg">WebCabz</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <a key={n.label} href={n.href} className="text-sm text-zinc-300 hover:text-white">{n.label}</a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" data-testid="header-login-link" className="text-sm text-zinc-300 hover:text-white">Sign in</Link>
          <Link to="/register" data-testid="header-start-link" className="bg-cab text-asphalt font-semibold text-sm px-4 py-2 rounded-md hover:brightness-105">
            Start Free Trial
          </Link>
        </div>
        <button data-testid="mobile-menu-toggle" className="md:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-asphalt border-t border-zinc-800 px-6 py-4 space-y-3">
          {NAV.map((n) => (
            <a key={n.label} href={n.href} className="block text-sm text-zinc-300" onClick={() => setOpen(false)}>{n.label}</a>
          ))}
          <Link to="/login" className="block text-sm text-zinc-300">Sign in</Link>
          <Link to="/register" className="inline-block bg-cab text-asphalt font-semibold text-sm px-4 py-2 rounded-md">Start Free Trial</Link>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section className="bg-asphalt text-white relative overflow-hidden">
      <div className="absolute inset-0 grid-asphalt opacity-60" />
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-cab/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-3 py-1.5 rounded-full text-xs text-zinc-300 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-cab animate-pulse" />
            Built for Indian cab operators · Now in early access
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-balance">
            Run your cab fleet like a <span className="text-cab">control room</span>.
          </h1>
          <p className="mt-6 text-lg text-zinc-300 max-w-xl text-pretty">
            WebCabz replaces your spreadsheets, WhatsApp groups, and paper trip-sheets with one unified
            platform for bookings, drivers, vehicles, GST invoicing and operations.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-cta-trial" className="bg-cab text-asphalt font-semibold px-5 py-3 rounded-md inline-flex items-center gap-2 hover:brightness-105">
              Start 14-day Free Trial <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/login" data-testid="hero-cta-demo" className="border border-zinc-700 text-white font-semibold px-5 py-3 rounded-md inline-flex items-center gap-2 hover:bg-zinc-900">
              View live demo
            </Link>
          </div>
          <div className="mt-8 flex items-center gap-6 text-xs text-zinc-400">
            <div className="flex items-center gap-2"><Check className="h-4 w-4 text-cab" /> No credit card</div>
            <div className="flex items-center gap-2"><Check className="h-4 w-4 text-cab" /> GST invoicing built-in</div>
            <div className="flex items-center gap-2"><Check className="h-4 w-4 text-cab" /> India-first</div>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute inset-0 bg-cab/20 blur-3xl rounded-3xl" />
            <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
              <div className="bg-zinc-950 px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <span className="ml-3 text-xs text-zinc-500 font-mono">webcabz.in/app/dashboard</span>
              </div>
              <div className="p-5 grid grid-cols-2 gap-3">
                {[
                  { l: "Today's Trips", v: "48", c: "text-cab" },
                  { l: "Running", v: "12", c: "text-emerald-400" },
                  { l: "Available", v: "23", c: "text-blue-400" },
                  { l: "Revenue", v: "₹1.8L", c: "text-cab" },
                ].map((s) => (
                  <div key={s.l} className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{s.l}</div>
                    <div className={`font-display font-bold text-3xl mt-1 ${s.c}`}>{s.v}</div>
                  </div>
                ))}
              </div>
              <div className="px-5 pb-5 space-y-2">
                {[
                  { id: "BK-2401-00128", route: "Pune Airport → Koregaon Park", status: "Running" },
                  { id: "BK-2401-00127", route: "Mumbai → Lonavala", status: "Confirmed" },
                  { id: "BK-2401-00126", route: "BKC → Andheri W", status: "Allocated" },
                ].map((b) => (
                  <div key={b.id} className="bg-zinc-950 border border-zinc-800 rounded-md p-3 flex items-center justify-between">
                    <div>
                      <div className="font-mono text-[11px] text-zinc-500">{b.id}</div>
                      <div className="text-sm text-zinc-200">{b.route}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-cab/20 text-cab">{b.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PartnerStrip() {
  const items = ["TRAVEL HOUSE", "CITY RIDES", "FLEETIQ", "DELHI WHEELS", "BANGALORE CABS", "MARATHA TRAVELS"];
  return (
    <div className="bg-asphalt border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <p className="text-center text-[11px] uppercase tracking-widest text-zinc-500 mb-6">
          Trusted by operators across 24 Indian cities
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 opacity-60">
          {items.map((i) => (
            <div key={i} className="text-center font-display font-bold text-zinc-400 text-sm tracking-wider">{i}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Features() {
  const feats = [
    { icon: Car, t: "Vehicle Management", d: "Track RC, Insurance, Fitness, Permit & PUC with automated expiry alerts at 90 / 60 / 30 / 7 days." },
    { icon: Users, t: "Driver Master", d: "License, badge, Aadhaar, police verification and medical records — fully digital with expiry tracking." },
    { icon: Calendar, t: "Smart Bookings", d: "Local · Airport · Outstation · Employee · School transport with conflict-aware driver allocation." },
    { icon: MapPin, t: "Trip Sheets", d: "Start KM, End KM, toll, parking, driver allowance — generate trip voucher PDFs in one tap." },
    { icon: FileText, t: "GST Billing", d: "HSN/SAC ready GST invoices, credit notes & tax reports tailored for Indian compliance." },
    { icon: BarChart3, t: "Real Reports", d: "Booking, revenue, vehicle utilisation, P&L and outstanding reports — export to Excel/PDF." },
    { icon: Shield, t: "Multi-tenant Secure", d: "Isolated tenant data, role-based access, login logs, device tracking and session control." },
    { icon: Bell, t: "Notifications", d: "In-app, SMS (MSG91) and WhatsApp Cloud API for bookings, allocations and renewals." },
  ];
  return (
    <section id="features" className="py-24 sm:py-32 bg-white border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">— What's inside</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-asphalt text-balance">
              Operations, paperwork, and money. Solved.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-zinc-600 text-lg leading-relaxed">
              Designed with cab operators from Pune, Mumbai, Bangalore and Delhi — WebCabz handles the
              60 things you do every day so your team can spend time growing the business.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
          {feats.map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-white p-6 hover:bg-zinc-50 transition-colors">
              <div className="h-10 w-10 rounded-md bg-cab/15 flex items-center justify-center mb-4">
                <Icon className="h-5 w-5 text-asphalt" />
              </div>
              <div className="font-semibold text-asphalt mb-1">{t}</div>
              <div className="text-sm text-zinc-600 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);
  return (
    <section id="pricing" className="py-24 sm:py-32 bg-zinc-50 border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">— Pricing</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-asphalt">
            Simple, INR pricing. Cancel anytime.
          </h2>
          <p className="text-zinc-600 mt-4 max-w-2xl mx-auto">14-day free trial on every plan. No setup fees. GST extra as applicable.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div
              key={p.id}
              data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`bg-white rounded-lg p-8 ${
                p.is_popular ? "border-2 border-cab shadow-lg relative" : "border border-zinc-200"
              }`}
            >
              {p.is_popular && (
                <div className="absolute -top-3 left-8 bg-cab text-asphalt text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                  Most Popular
                </div>
              )}
              <div className="font-display font-bold text-2xl text-asphalt">{p.name}</div>
              <div className="text-sm text-zinc-500 mt-1">{p.description}</div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display font-black text-5xl text-asphalt">{formatINR(p.price)}</span>
                <span className="text-zinc-500 text-sm">/month</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-700">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {p.vehicle_limit >= 9999 ? "Unlimited" : p.vehicle_limit} vehicles</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {p.driver_limit >= 9999 ? "Unlimited" : p.driver_limit} drivers</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {p.user_limit >= 9999 ? "Unlimited" : p.user_limit} team users</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {p.booking_limit >= 999999 ? "Unlimited" : p.booking_limit.toLocaleString("en-IN")} bookings/mo</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> GST billing & reports</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> Email & in-app notifications</li>
              </ul>
              <Link
                to="/register"
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                className={`mt-8 block text-center font-semibold px-5 py-3 rounded-md ${
                  p.is_popular ? "bg-cab text-asphalt" : "bg-asphalt text-white"
                }`}
              >
                Start free trial
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const items = [
    {
      quote: "We moved from 4 spreadsheets and 3 WhatsApp groups to WebCabz. My ops manager saves 3 hours every single day.",
      name: "Rakesh Sharma", role: "Owner, Sunrise Travels", city: "Pune",
    },
    {
      quote: "The driver allocation conflict-check is gold. We used to double-book vehicles every week. Not anymore.",
      name: "Priya Patil", role: "Operations Head, City Rides", city: "Mumbai",
    },
    {
      quote: "Document expiry alerts saved us from two challan situations last month. Worth the subscription on day one.",
      name: "Vikram Singh", role: "Fleet Manager, Delhi Wheels", city: "Delhi NCR",
    },
  ];
  return (
    <section id="testimonials" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-14">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">— Customers</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-asphalt">
            Operators who switched, didn't go back.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((t) => (
            <div key={t.name} className="bg-zinc-50 border border-zinc-200 rounded-lg p-6">
              <div className="flex gap-1 mb-4">
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-4 w-4 text-cab fill-cab" />)}
              </div>
              <p className="text-zinc-700 leading-relaxed mb-6">"{t.quote}"</p>
              <div className="flex items-center gap-3 pt-4 border-t border-zinc-200">
                <div className="h-10 w-10 rounded-full bg-asphalt text-cab flex items-center justify-center font-display font-bold">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-asphalt text-sm">{t.name}</div>
                  <div className="text-xs text-zinc-500">{t.role} · {t.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const qa = [
    { q: "Is there really a free trial?", a: "Yes — 14 days, no credit card required. You can use every feature in the Professional plan during the trial." },
    { q: "Can I import my existing data?", a: "Yes. We support CSV imports for vehicles, drivers and clients. For larger fleets (200+) we'll do white-glove onboarding for free." },
    { q: "Do you support GST invoicing?", a: "Yes — HSN/SAC mapping, GSTIN on invoices, IGST/CGST/SGST split, and tax reports for filing." },
    { q: "Is my data isolated from other tenants?", a: "Yes. WebCabz is a true multi-tenant SaaS — every record is scoped to your tenant_id and never crosses." },
    { q: "Which payment methods do you accept?", a: "All major UPI apps, Indian credit/debit cards, net banking and wallets via Razorpay." },
  ];
  return (
    <section id="faq" className="py-24 sm:py-32 bg-zinc-50 border-y border-zinc-200">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">— Questions</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-asphalt">Everything else</h2>
        </div>
        <div className="space-y-3">
          {qa.map((item, idx) => (
            <details key={idx} className="bg-white border border-zinc-200 rounded-lg group">
              <summary className="cursor-pointer px-6 py-4 font-semibold text-asphalt list-none flex items-center justify-between">
                {item.q}
                <span className="text-cab text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <div className="px-6 pb-5 text-zinc-600">{item.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="bg-asphalt relative overflow-hidden">
      <div className="absolute inset-0 grid-asphalt opacity-40" />
      <div className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full bg-cab/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 text-white">
          <h2 className="font-display font-black text-5xl sm:text-6xl tracking-tighter text-balance">
            Ready to run a <span className="text-cab">tighter ship</span>?
          </h2>
          <p className="mt-5 text-zinc-300 max-w-xl">
            Join Indian cab operators who quit spreadsheets, moved to WebCabz, and added 2 hours back to their day.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="footer-cta-trial" className="flex-1 bg-cab text-asphalt font-semibold px-5 py-4 rounded-md inline-flex items-center justify-center gap-2">
              Start 14-day trial <Zap className="h-4 w-4" />
            </Link>
            <Link to="/login" data-testid="footer-cta-demo" className="flex-1 border border-zinc-700 text-white font-semibold px-5 py-4 rounded-md inline-flex items-center justify-center">
              See live demo
            </Link>
          </div>
          <div className="mt-4 text-xs text-zinc-500">
            <Building2 className="h-3.5 w-3.5 inline mr-1" /> demo@webcabz.in · Demo@123 — explore the demo tenant
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-asphalt border-t border-zinc-900 text-zinc-500 py-12">
      <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 bg-cab flex items-center justify-center rounded font-display font-black text-asphalt">W</span>
          <span className="font-display font-bold text-white">WebCabz</span>
          <span className="text-xs ml-3">© {new Date().getFullYear()} · Made in India</span>
        </div>
        <div className="flex items-center gap-6 text-xs">
          <a href="#" className="hover:text-white">Privacy</a>
          <a href="#" className="hover:text-white">Terms</a>
          <a href="#" className="hover:text-white">Status</a>
          <a href="#" className="hover:text-white">support@webcabz.in</a>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-white">
      <Header />
      <Hero />
      <PartnerStrip />
      <Features />
      <Pricing />
      <Testimonials />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
